import React, { useEffect, useState, useRef } from 'react';
import DataTable from 'react-data-table-component';
import { CSVLink } from 'react-csv';
import { getRequest, postPayURequest } from '../../../Utils/AppApi';
import { useHistory } from "react-router-dom";
import { Popconfirm, DatePicker, Tooltip, message } from 'antd';
import { ReloadOutlined, FileTextOutlined } from '@ant-design/icons';

import { BBPS_IMWALLET_ADMIN_TRANSACTION_HISTORY, BBPS_IMWALLET_TRANSACTION_STATUS } from '../../../Utils/appConstants';
import { getUTCToLocalDateTimeFormat, toDayDate, queryDate, getExportFormat } from '../../../Utils/function';

function AdminBbpsImWalletReport() {
  const history = useHistory();
  const csvLink = useRef();
  const limit = 10;
  const dateFormat = 'DD-MM-YYYY';
  const [endDate, setEndDate] = useState(toDayDate());
  const [startDate, setStartDate] = useState(toDayDate());
  const [getData, setData] = useState([]);
  const [getTotalRecord, setTotalRecord] = useState(0);
  const [page, setPage] = useState(1);
  const [isLimit, setLimit] = useState(limit);
  const [searchTransId, setSearchTransId] = useState('');
  const [searchAccount, setSearchAccount] = useState('');
  const [searchMobile, setSearchMobile] = useState('');
  const [searchUserName, setSearchUserName] = useState('');
  const [searchBy, setSearchBy] = useState('ID');
  const [isLoading, setLoading] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [fileName, setFileName] = useState('bbps_report');

  // Function to generate filename based on selected dates
  const generateFileName = () => {
    if (startDate && endDate) {
      const fromDate = queryDate(startDate);
      const toDate = queryDate(endDate);
      
      // Check if dates are valid
      if (fromDate && fromDate !== 'Invalid date' && toDate && toDate !== 'Invalid date') {
        return `bbps_report_${fromDate}_to_${toDate}`;
      }
    }
    // Default filename when no valid dates are selected
    return 'bbps_report';
  };

  const columns = [
    {
      name: 'Date & Time',
      cell: row => getUTCToLocalDateTimeFormat(row.createdAt),
      width: "160px"
    },
    {
      name: 'User Name',
      selector: row => row.userName,
      width: "150px"
    },
    {
      name: 'Username',
      selector: row => row.userUsername,
      width: "120px"
    },
    {
      name: 'API',
      selector: row => row.apiProvider,
      width: "60px"
    },
    {
      name: 'TXT ID',
      selector: row => <Tooltip title={row.txtId}>{row.txtId}</Tooltip>,
      width: "180px"
    },
    {
      name: 'Customer Name',
      selector: row => row.customerName,
      width: "150px"
    },
    {
      name: 'Account Number',
      selector: row => row.accountNumber,
      width: "160px"
    },
    {
      name: 'Biller Name',
      selector: row => row.billerName,
      width: "200px"
    },
    {
      name: 'Category',
      selector: row => row.category,
      width: "120px"
    },
    {
      name: 'Mobile',
      selector: row => row.customerPhoneNumber,
      width: "140px"
    },
    {
      name: 'Amount',
      selector: row => row.paidAmount ? `₹${row.paidAmount}` : '-',
      width: "100px"
    },
    {
      name: 'Action',
      cell: row => (
        <div style={{ display: 'flex', gap: '8px' }}>
          {row.txnStatus === 'PENDING' && (
            <Popconfirm title="Check transaction status?" onConfirm={() => handleStatusCheck(row.refId)} okText="Yes" cancelText="No">
              <Tooltip title="Check Status">
                <div className="icon-border border border-warning text-warning pointer"><ReloadOutlined /></div>
              </Tooltip>
            </Popconfirm>
          )}
        </div>
      ),
      width: "120px"
    },
    {
      name: 'Status',
      cell: row => {
        let color = '';
        if (row.txnStatus === 'PAYMENT_SUCCESS') color = 'green';
        else if (row.txnStatus === 'PENDING') color = 'orange';
        else if (row.txnStatus === 'PAYMENT_FAILURE') color = 'red';
        else color = 'gray';
        return <span style={{ color, fontWeight: 500 }}>{row.txnStatus?.replace(/_/g, ' ') || 'N/A'}</span>;
      },
      width: "140px"
    }
  ];

  const handleStatusCheck = async (refId) => {
    try {
      const response = await postPayURequest(BBPS_IMWALLET_TRANSACTION_STATUS, { refId }, history);
      if (response.success === true) {
        message.success(response.message || 'Status updated');
        getDataReports();
      } else {
        message.error(response.message || 'Failed to update status');
      }
    } catch (error) {
      console.error(error);
      message.error('Error checking status');
    }
  };

  const objectToQueryString = (obj) => {
    const str = [];
    for (const p in obj) {
      if (obj.hasOwnProperty(p) && obj[p]) {
        str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
      }
    }
    return str.join("&");
  };

  const getDataReports = (rowsPerPage) => {
    if (rowsPerPage) setLimit(rowsPerPage);
    const params = {
      page: page,
      limit: isLimit,
      startDate: startDate ? queryDate(startDate) : '',
      endDate: endDate ? queryDate(endDate) : ''
    };

    if (searchBy === 'ID' && searchTransId) {
      params.search = searchTransId;
      params.searchBy = 'ID';
    } else if (searchBy === 'account_number' && searchAccount) {
      params.search = searchAccount;
      params.searchBy = 'account_number';
    } else if (searchBy === 'mobile' && searchMobile) {
      params.search = searchMobile;
      params.searchBy = 'mobile';
    } else if (searchBy === 'user_name' && searchUserName) {
      params.search = searchUserName;
      params.searchBy = 'user_name';
    }

    const queryString = objectToQueryString(params);
    setLoading(true);
    getRequest(`${BBPS_IMWALLET_ADMIN_TRANSACTION_HISTORY}?${queryString}`, history)
      .then((response) => {
        if (response.success === true) {
          setData(response.data.docs || []);
          setTotalRecord(response.data.totalDocs || 0);
          setPage(response.data.page || 1);
        } else {
          setData([]);
          setTotalRecord(0);
          setPage(1);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
      });
  };

  const getCSVDataList = () => {
    const params = {
      exportsCsv: true,
      limit: 1000000,
      startDate: startDate ? queryDate(startDate) : '',
      endDate: endDate ? queryDate(endDate) : ''
    };

    if (searchBy === 'ID' && searchTransId) {
      params.search = searchTransId;
      params.searchBy = 'ID';
    } else if (searchBy === 'account_number' && searchAccount) {
      params.search = searchAccount;
      params.searchBy = 'account_number';
    } else if (searchBy === 'mobile' && searchMobile) {
      params.search = searchMobile;
      params.searchBy = 'mobile';
    } else if (searchBy === 'user_name' && searchUserName) {
      params.search = searchUserName;
      params.searchBy = 'user_name';
    }

    const queryString = objectToQueryString(params);
    getRequest(`${BBPS_IMWALLET_ADMIN_TRANSACTION_HISTORY}?${queryString}`, history)
      .then((response) => {
        if (response.success === true) {
          const records = response.data.docs || [];
          const csvRows = records.map(row => ({
            "Date & Time": getExportFormat(row.createdAt),
            "User Name": row.userName,
            "Username": row.userUsername,
            "API Provider": row.apiProvider,
            "Transaction ID": row.txtId,
            "Customer Name": row.customerName,
            "Account Number": row.accountNumber,
            "Biller Name": row.billerName,
            "Category": row.category,
            "Mobile": row.customerPhoneNumber,
            "Amount": row.paidAmount ? row.paidAmount : '-',
            "Status": row.txnStatus?.replace(/_/g, ' ') || 'N/A'
          }));
          setCsvData(csvRows);
          
          // Set filename based on selected dates
          const newFileName = generateFileName();
          setFileName(newFileName);
          
          setTimeout(() => {
            if (csvLink.current) {
              csvLink.current.link.click();
            }
          }, 500);
        } else {
          console.error("CSV export failed");
          message.error("Failed to export data");
        }
      })
      .catch((error) => {
        console.error(error);
        message.error("Error exporting data");
      });
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const onChangeStartDate = (date) => {
    setStartDate(date);
  };

  const onChangeEndDate = (date) => {
    setEndDate(date);
  };

  const handleSearch = () => {
    setPage(1);
    getDataReports();
  };

  const handleClear = () => {
    setStartDate(toDayDate());
    setEndDate(toDayDate());
    setSearchTransId('');
    setSearchAccount('');
    setSearchMobile('');
    setSearchUserName('');
    setSearchBy('ID');
    setPage(1);
    setTimeout(() => {
      getDataReports();
    }, 100);
  };

  useEffect(() => {
    getDataReports();
  }, [page]);

  return (
    <div className="content-wrapper pt-2">
      <div className="content-header my-0 py-0">
        <div className="container-fluid">
          <div className="row mb-0 align-items-center">
            <div className="col-sm-6">
              <h1 className='page-title text-blue font-merriweather'>BBPS <span className='text-red'>Transaction Report</span></h1>
            </div>
          </div>
        </div>
      </div>
      <div className="content">
        <div className="container-fluid pt-2 pb-5">
          <div className="row">
            <div className='card col-md-12 py-3'>
              <div className='card-body p-0'>
                <div className="row">
                  <div className='col-md-10 date-search mb-0'>
                    <div className='row'>
                      <div className='col-md-3 col-lg-3 d-flex'>
                        <div className="form-group col-6 mr-3">
                          <label className='date-search-label mb-0 text-blue'>From</label>
                          <DatePicker className="form-control form-control-border" value={startDate} format={dateFormat} onChange={onChangeStartDate} />
                        </div>
                        <div className="form-group col-6">
                          <label className='date-search-label mb-0 text-red'>To</label>
                          <DatePicker className="form-control form-control-border" value={endDate} format={dateFormat} onChange={onChangeEndDate} />
                        </div>
                      </div>
                      <div className='col-md-2 col-lg-2'>
                        <div className="form-group">
                          <label className='date-search-label mb-0 text-blue'>Search By</label>
                          <select className="form-control form-control-border" value={searchBy} onChange={(e) => setSearchBy(e.target.value)}>
                            <option value="ID">Transaction ID</option>
                            <option value="account_number">Account Number</option>
                            <option value="mobile">Mobile</option>
                            <option value="user_name">User Name</option>
                          </select>
                        </div>
                      </div>
                      <div className='col-md-3 col-lg-3'>
                        <div className="form-group">
                          <label className='date-search-label mb-0 text-red'>&nbsp;</label>
                          <input
                            type="text"
                            className="form-control form-control-border"
                            placeholder={searchBy === 'ID' ? "Transaction ID" : (searchBy === 'account_number' ? "Account Number" : (searchBy === 'mobile' ? "Mobile Number" : "User Name"))}
                            value={
                              searchBy === 'ID' ? searchTransId :
                                searchBy === 'account_number' ? searchAccount :
                                  searchBy === 'mobile' ? searchMobile : searchUserName
                            }
                            onChange={(e) => {
                              const val = e.target.value;
                              if (searchBy === 'ID') setSearchTransId(val);
                              else if (searchBy === 'account_number') setSearchAccount(val);
                              else if (searchBy === 'mobile') setSearchMobile(val);
                              else setSearchUserName(val);
                            }}
                          />
                        </div>
                      </div>
                      <div className='col-md-1 col-lg-1'>
                        <div><label className='date-search-label mb-0 text-red'>&nbsp;</label></div>
                        <button type="button" className="btn btn-primary rounded-pill pl-4 pr-4" onClick={handleSearch}>Search</button>
                      </div>
                      <div className='col-md-1 col-lg-1'>
                        <div><label className='date-search-label mb-0 text-red'>&nbsp;</label></div>
                        <button type="button" className="btn btn-default rounded-pill px-4" onClick={handleClear}>Clear</button>
                      </div>
                    </div>
                  </div>
                  <div className='col-md-2'>
                    <div className='float-sm-right'>
                      <button type="button" className="btn btn-primary mr-3" onClick={getCSVDataList}>
                        <i className="fa fa-file-excel"></i> Export
                      </button>
                      <CSVLink
                        data={csvData}
                        filename={fileName}
                        className='hidden'
                        ref={csvLink}
                        target='_blank'
                      />
                    </div>
                  </div>
                </div>
                <div className="container-fluid pt-2 pb-5">
                  <DataTable
                    title={""}
                    columns={columns}
                    data={getData}
                    highlightOnHover
                    pagination
                    paginationServer
                    progressPending={isLoading}
                    paginationTotalRows={getTotalRecord}
                    onChangeRowsPerPage={getDataReports}
                    onChangePage={handlePageChange}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminBbpsImWalletReport;